import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardButton } from 'base/StandardButton/StandardButton';
import SearchBox from 'components/common/SearchBox';
import {
  APPLICATION_TYPE_OPTION_FORM,
  APPLICATION_TYPE_OPTION_ID
} from 'constant/RoutesUrls';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter, checkAccess } from 'utils/helpers';
import * as applicationOptionAction from '../../redux/applicationTypeOption/applicationTypeAction';
import {
  applicationTypeOptionInterface,
  paramsI
} from '../../../types/interface';
import { useAppDispatch } from '../../redux/HooksRedux';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import CustomAdvanceTableFooter from 'components/customComponent/CustomAdvanceTableFooter';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';
import { StandardLoader } from 'base/Loader/Loader';
import { Dropdown } from 'react-bootstrap';
import { UilEllipsisV } from '@iconscout/react-unicons';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import SeeMoreText from 'components/base/SeeMoreText';
import { Table } from 'react-bootstrap';
import { SortingState } from '@tanstack/react-table';
import {
  ACCENDING,
  ARCHIVE,
  DESCENDING,
  modules,
  ViewAllLimit
} from 'utils/enums/misc';
import { archiveSearchOptions, debounceTime } from 'utils/staticData';
import StandardSelect from 'base/StandardSelect/StandardSelect';
import Badge from 'components/base/Badge';

export const ApplicationTypeOption = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { totalPages, totalRecords } = useSelector(
    (state: RootState) => state?.applicationTypeSliceData || 0
  );
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );

  const [searchText, setSearchText] = useState<string>('');
  const [pageCount, setPageCount] = useState<number>(1);
  const [dataPerPage, setDataPerPage] = useState<number>(10);
  const [archiveSearchValue, setArchiveSearchValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAllVisibleData, setIsAllVisibleData] = useState(false);
  const [applicationOptionList, setApplicationOptionList] = useState<
    applicationTypeOptionInterface[]
  >([]);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [id, setId] = useState<number>();
  const [sorting, setSorting] = useState<SortingState>();

  const getAllApplicationOptionWithProduct = async () => {
    const params: paramsI = {
      page: pageCount,
      limit: isAllVisibleData ? ViewAllLimit : dataPerPage,
      keyword: searchText,
      sortBy: sorting?.length ? sorting[0].id : undefined,
      is_archived: archiveSearchValue === ARCHIVE,
      sortOrder: (() => {
        if (!sorting?.length) return undefined;
        return sorting[0].desc ? DESCENDING : ACCENDING;
      })()
    };
    setIsLoading(true);
    try {
      const response = await dispatch(
        applicationOptionAction.getApplicationTypeOptionAll(params)
      );
      if (response?.status === 200) {
        setApplicationOptionList(response?.data.request_options);
        if (pageCount > response?.data?.meta?.totalPages) {
          setPageCount(1);
        }
        setIsLoading(false);
      } else {
        errorNotification(response.data.message);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const handleEdit = (optionId: number) => {
    navigate(
      `${APPLICATION_TYPE_OPTION_ID.replace(':optionId', optionId?.toString())}`
    );
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        applicationOptionAction.deleteApplicationTypeOption(id!)
      );

      if (response?.status === 200) {
        successNotification(response.data?.message);
        setShowDeletePopup(false);
        getAllApplicationOptionWithProduct();
        setIsLoading(false);
      } else {
        errorNotification(response?.data?.message);
        setShowDeletePopup(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      setShowDeletePopup(false);
    }
  };

  const debouncedSearch = useCallback(() => {
    const handler = setTimeout(() => {
      getAllApplicationOptionWithProduct();
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [
    searchText,
    pageCount,
    sorting,
    dataPerPage,
    isAllVisibleData,
    archiveSearchValue
  ]);

  useEffect(() => {
    setIsLoading(true);
    const cleanup = debouncedSearch();
    return cleanup;
  }, [
    searchText,
    pageCount,
    sorting,
    dataPerPage,
    isAllVisibleData,
    archiveSearchValue
  ]);

  const columns: ColumnDef<applicationTypeOptionInterface>[] = [
    {
      id: 'index',
      accessorKey: t('applications.table.title_1'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('applications.table.title_1')}</span>
      ),
      cell: ({ row: { index } }) => <div className="py-2">{index + 1}</div>
    },
    {
      id: 'name',
      accessorKey: t('application_type_option.table.title_2'),
      header: () => (
        <span className="text-nowrap">
          {t('application_type_option.table.title_2')}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return <div>{original.name}</div>;
      }
    },
    {
      id: 'product',
      accessorKey: t('general.common_text.text_12'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_12')}</span>
      ),
      cell: ({ row: { original } }) => {
        const elements = [];
        for (let index = 0; index < original.product.length; index++) {
          const item = original.product[index];

          if (index <= 3) {
            elements.push(<div key={item.product_id}>{item.product_name}</div>);
          } else if (index === 4) {
            elements.push(
              <SeeMoreText
                headerText={t('general.common_text.text_12')}
                inputType="other"
                maxChars={38}
                key={index}
              >
                <Table bordered className=" border-secondary-subtle" responsive>
                  <tbody>
                    {original.product.map((item, index) => (
                      <tr key={index}>
                        <td className="text-body ps-2 fs-8">
                          {item.product_name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </SeeMoreText>
            );
            break;
          }
        }
        return elements;
      }
    },
    {
      id: 'status',
      accessorKey: t('general.common_text.text_7'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_7')}</span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <ConditionalRender
            condition={!original.is_archived}
            fallBack={
              <Badge variant="phoenix" bg="danger">
                {t('common_table.text_5')}
              </Badge>
            }
          >
            <Badge variant="phoenix" bg="success" className="py-1">
              {t('common_table.text_4')}
            </Badge>
          </ConditionalRender>
        );
      }
    },
    {
      id: 'action',
      accessorKey: t('general.common_text.text_8'),
      enableSorting: false,
      header: () => (
        <ConditionalRender
          condition={checkAccess(
            [
              modules.APPLICATION_REQUEST_OPTION.edit,
              modules.APPLICATION_REQUEST_OPTION.delete
            ],
            userPermission
          )}
        >
          <span className="text-nowrap">{t('general.common_text.text_8')}</span>
        </ConditionalRender>
      ),
      cell: ({ row: { original } }) => {
        return (
          <ConditionalRender
            condition={checkAccess(
              [
                modules.APPLICATION_REQUEST_OPTION.edit,
                modules.APPLICATION_REQUEST_OPTION.delete
              ],
              userPermission
            )}
          >
            <Dropdown
              id="scroll_in_table"
              placement="auto"
              className="position-static"
            >
              <Dropdown.Toggle
                variant="link"
                className="no-padding no-margin ps-0 d-flex"
              >
                <UilEllipsisV className="text-body" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="p-0">
                <ConditionalRender
                  condition={checkAccess(
                    [modules.APPLICATION_REQUEST_OPTION.edit],
                    userPermission
                  )}
                >
                  <Dropdown.Item
                    className="pe-0 border-bottom"
                    onClick={() => handleEdit(original?.application_request_id)}
                  >
                    <span className="d-flex fs-16 p-0 font-normal">
                      <p className="mb-0">
                        {capitalizeFirstLetter(
                          t('general.common_text.text_13')
                        )}
                      </p>
                    </span>
                  </Dropdown.Item>
                </ConditionalRender>
                <ConditionalRender
                  condition={checkAccess(
                    ['application-request-option.delete'],
                    userPermission
                  )}
                >
                  <Dropdown.Item
                    onClick={() => {
                      setId(original?.application_request_id);
                      setShowDeletePopup(true);
                    }}
                  >
                    <span className="d-flex fs-16 font-normal p-0">
                      <p className="mb-0">
                        {capitalizeFirstLetter(
                          t('general.common_text.text_14')
                        )}
                      </p>
                    </span>
                  </Dropdown.Item>
                </ConditionalRender>
              </Dropdown.Menu>
            </Dropdown>
          </ConditionalRender>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: applicationOptionList || [],
    columns: columns,
    selection: false,
    sortable: true,
    pageSize: dataPerPage,
    pagination: true,
    sorting: sorting,
    setSorting: setSorting
  });

  const renderDeleteModal = () => {
    if (showDeletePopup) {
      return (
        <DeleteModel
          onConfirm={handleDelete}
          onCancel={() => setShowDeletePopup(false)}
        />
      );
    }
  };
  return (
    <>
      <div className="d-flex flex-wrap justify-content-between mb-2 align-items-center flex-column flex-md-row">
        <div className="w-auto">
          <h3 className="m-0">
            {capitalizeFirstLetter(t('header.text.text_6'))}{' '}
          </h3>
        </div>
        <div className="d-flex gap-2 w-auto flex-column flex-md-row">
          <SearchBox
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <StandardSelect
            value={archiveSearchValue}
            className="archive_search "
            onChange={e => {
              if (Array.isArray(e) && e.length === 0) {
                setArchiveSearchValue('');
              } else {
                setArchiveSearchValue(e);
              }
              setPageCount(1);
            }}
            options={archiveSearchOptions}
            placeholder={t('product.placeholder.text_2')}
            isClearable={true}
            isSearchable={false}
            isMulti={false}
            formClassName="w-330 "
            id=""
            label=""
            showError={false}
            validations={[]}
          />
          <ConditionalRender
            condition={checkAccess(
              [modules.APPLICATION_REQUEST_OPTION.create],
              userPermission
            )}
          >
            <StandardButton
              buttonText={capitalizeFirstLetter(
                t('application_type_option.button.text_1')
              )}
              onClick={() => {
                navigate(APPLICATION_TYPE_OPTION_FORM);
              }}
            />
          </ConditionalRender>
        </div>
      </div>
      <div>
        <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
          <div className="px-4 py-1 px-lg-6 bg-body-emphasis scrollbar overflow-auto rounded border-top border-bottom border-left border-right border position-relative top-1">
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                tableProps={{
                  className: 'phoenix-table fs-9 p-0',
                  responsive: true
                }}
                dataLength={applicationOptionList.length}
              />
              <div className="my-3 ps-0">
                <ConditionalRender condition={totalPages > 0}>
                  <CustomAdvanceTableFooter
                    pagination
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    recordsPerPage={dataPerPage}
                    currentPage={pageCount}
                    setCurrentPage={setPageCount}
                    CanPreviousPage={() => pageCount > 1}
                    CanNextPage={() => totalPages > pageCount}
                    handleNextPage={() => setPageCount(pageCount + 1)}
                    handlePreviousPage={() => setPageCount(pageCount - 1)}
                    showViewAllBtn={true}
                    setRecordsPerPage={setDataPerPage}
                    isAllVisibleData={isAllVisibleData}
                    setIsAllVisibleData={setIsAllVisibleData}
                    isLoading={isLoading}
                  />
                </ConditionalRender>
              </div>
            </AdvanceTableProvider>
          </div>
        </ConditionalRender>
      </div>
      {renderDeleteModal()}
    </>
  );
};
